import createDebugLogger from 'debug';

import * as Constants from './constants';
import { Levels, TransporterFactory } from './types';

export const ENABLER_KEY = 'DOCK:browser-console-transporter';

/**
 * Make a browser logger based on debug package
 * @param namespace namespace of the logger
 * @param level level of the logger
 * @returns a logger
 */
const createLogger = (level: Levels) => {
  const logger = createDebugLogger('');
  // eslint-disable-next-line no-console
  logger.log = console[level].bind(console);
  logger.enabled = true;
  return logger;
};

export const isLevelInitialized = () => {
  const allowedMinConfiguredLevel = localStorage.getItem(
    ENABLER_KEY,
  ) as Levels | null;

  return !!allowedMinConfiguredLevel;
};

export const setMinAllowedLevel = (level: Levels) => {
  if (!Constants.LOG_LEVELS.includes(level)) {
    throw new Error(`Invalid log level: ${level}`);
  }

  localStorage.setItem(ENABLER_KEY, level);
};

const isLevelAllowed = (level: Levels) => {
  const allowedMinConfiguredLevel = localStorage.getItem(
    ENABLER_KEY,
  ) as Levels | null;

  return (
    !!allowedMinConfiguredLevel &&
    Constants.LOG_LEVELS.indexOf(level) >=
      Constants.LOG_LEVELS.indexOf(allowedMinConfiguredLevel)
  );
};

/**
 * @returns a transporter that logs to the browser console
 */
const browserConsoleTransporter: TransporterFactory = () => {
  if (!isLevelInitialized()) {
    setMinAllowedLevel(Constants.LOG_LEVEL_DEBUG);
  }
  const debugLogger = createLogger(Constants.LOG_LEVEL_DEBUG);
  const infoLogger = createLogger(Constants.LOG_LEVEL_INFO);
  const warnLogger = createLogger(Constants.LOG_LEVEL_WARN);
  const errorLogger = createLogger(Constants.LOG_LEVEL_ERROR);

  return {
    debug: (transporterLog) => {
      if (isLevelAllowed(Constants.LOG_LEVEL_DEBUG)) {
        debugLogger.namespace = `${transporterLog.namespace}:${Constants.LOG_LEVEL_DEBUG}`;
        debugLogger(transporterLog.message, ...transporterLog.optionalParams);
      }
    },
    info: (transporterLog) => {
      if (isLevelAllowed(Constants.LOG_LEVEL_INFO)) {
        infoLogger.namespace = `${transporterLog.namespace}:${Constants.LOG_LEVEL_INFO}`;
        infoLogger(transporterLog.message, ...transporterLog.optionalParams);
      }
    },
    warn: (transporterLog) => {
      if (isLevelAllowed(Constants.LOG_LEVEL_WARN)) {
        warnLogger.namespace = `${transporterLog.namespace}:${Constants.LOG_LEVEL_WARN}`;
        warnLogger(transporterLog.message, ...transporterLog.optionalParams);
      }
    },
    error: (transporterLog) => {
      if (isLevelAllowed(Constants.LOG_LEVEL_ERROR)) {
        errorLogger.namespace = `${transporterLog.namespace}:${Constants.LOG_LEVEL_ERROR}`;
        errorLogger(transporterLog.message, ...transporterLog.optionalParams);
      }
    },
  };
};

export default browserConsoleTransporter;
